import React from 'react'
import kebabcase from 'lodash.kebabcase'

import { sortBy, prop, filter, values, partition } from 'ramda'
import { OrderedMap } from 'immutable'
import { Select } from '@zambezi/sdk/dropdown-list'

import { renewSessionToken$ } from '@apiEnabler/ob-common/lib/intents'
import DateTimeWarning from '@apiEnabler/ob-common/lib/view/DateTimeWarning'

import ConsentListSectionHeader from './ConsentListSectionHeader'
import ConsentEmptyListSectionHeader from './ConsentEmptyListSectionHeader'

import style from './CategorisedConsents.module.css'

const defaultFilterLabel = 'All statuses'

const friendlyGrouping = {
  Created: 'Created',
  Active: 'Active',
  Confirmed: 'Active',
  Revoked: 'Cancelled',
  Suspended: 'Cancelled',
  Deleted: 'Cancelled',
  Cancelled: 'Cancelled',
  Blocked: 'Cancelled',
  Declined: 'Cancelled',
  Expired: 'Expired',
}

const friendlyGroupingOrder = ['Created', 'Active', 'Cancelled', 'Expired']

export default class CategorisedConsents extends React.Component {
  constructor(props) {
    super(props)

    this.state = { activeFilterName: defaultFilterLabel }
  }

  render() {
    const {
      Card,
      handleDisplayStatusesMeaning,
      tpp,
      consentType,
      consents,
      sections,
    } = this.props
    const { activeFilterName } = this.state
    const onSelectFilter = f => {
      this.setState({ activeFilterName: f.name })
      renewSessionToken$.next()
    }

    const defaultFilter = {
      label: defaultFilterLabel,
      name: defaultFilterLabel,
      consents,
      noOfConsents: consents.size,
      order: -1, // default filter at the top
    }
    const statusFilters = {
      [defaultFilterLabel]: defaultFilter,
      ...filter(x => x.noOfConsents, sections),
    }

    const activeFilter = statusFilters[activeFilterName]

    const visibleSections =
      activeFilterName === defaultFilterLabel
        ? values(sections)
        : [sections[activeFilterName]]

    const groupedSections =
      groupCancelledRevokedSuspendedDeleted(visibleSections)
    const [nonEmptySections, emptySections] = partition(
      s => s.noOfConsents > 0,
      groupedSections
    )

    const emptyStatusLabels = emptySections.map(s => s.label)

    return (
      <section className="js-categorised-consents">
        <div className={style.selectFilterSection}>
          <div className={style.selectFilterContainer}>
            <button
              onClick={handleDisplayStatusesMeaning}
              className={`${style.link} zb-button-link js-status-meaning-link`}
            >
              What do these mean?
            </button>
            <Select
              aria-label="Consent status"
              defaultValue={`${activeFilter.label} (${activeFilter.noOfConsents})`}
              suggestions={sortBy(prop('order'), values(statusFilters))}
              getSuggestionValue={d => `${d.label} (${d.noOfConsents})`}
              onSuggestionSelected={(e, { suggestion }) =>
                onSelectFilter(suggestion)
              }
            />
          </div>
        </div>
        <div className={style.sections}>
          {nonEmptySections.map(({ label, consents, info }, i) => (
            <section
              key={i}
              className={`${style.section} js-section-for-${kebabcase(label)}`}
            >
              <ConsentListSectionHeader
                consentType={consentType}
                count={consents.size}
                label={label}
                info={info}
              />
              {consents
                .valueSeq()
                .map((consent, index) => (
                  <Card key={index} tpp={tpp} consent={consent} index={index} />
                ))
                .toArray()}
            </section>
          ))}
        </div>
        <div className={style.sections}>
          {emptySections.length > 0 && (
            <ConsentEmptyListSectionHeader
              labels={emptyStatusLabels}
              consentType={consentType}
            />
          )}
        </div>
        <DateTimeWarning />
      </section>
    )
  }
}

function groupCancelledRevokedSuspendedDeleted(sections) {
  const grouped = sections.reduce(clump, {})
  return friendlyGroupingOrder.reduce((acc, label) => {
    const group = grouped[label] || { label }
    const { consents } = group
    if (!consents || !consents.size) return acc
    group.noOfConsents = consents.size
    acc.push(group)
    return acc
  }, [])
}

function clump(acc, next) {
  const { label } = next
  const friendlyLabel = friendlyGrouping[label]
  const currentGroup = acc[friendlyLabel] || {
    label: friendlyLabel,
    consents: OrderedMap(),
    info: next.info,
  }
  currentGroup.consents = currentGroup.consents.merge(next.consents)
  acc[friendlyLabel] = currentGroup
  return acc
}
